import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Sydney timezone identifier
const SYDNEY_TIMEZONE = 'Australia/Sydney';

// Convert UTC date to Sydney time - also handles Excel serial numbers
function toSydneyTime(dateStr) {
  if (!dateStr) return null;
  
  // Handle Excel serial numbers (integer between 1 and 100000)
  const numValue = Number(dateStr);
  if (!isNaN(numValue) && numValue > 0 && numValue < 100000 && Number.isInteger(numValue)) {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
    if (!isNaN(date.getTime())) return date;
  }
  
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date;
}

// Standard date format: DD-MMM-YYYY (e.g., 19-Feb-2026) in Sydney time
export function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = toSydneyTime(dateStr);
  if (!date) return dateStr;
  
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: SYDNEY_TIMEZONE
  }).replace(/\s/g, '-');
}

// Short date format: DD-MMM-YY (e.g., 19-Feb-26) in Sydney time
export function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const date = toSydneyTime(dateStr);
  if (!date) return dateStr;
  
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    timeZone: SYDNEY_TIMEZONE
  });
}

// Standard time format: HH:MM (24-hour) in Sydney time
export function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = toSydneyTime(dateStr);
  if (!date) return '';
  
  return date.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: SYDNEY_TIMEZONE
  });
}

// Combined date and time: DD-MMM-YYYY HH:MM in Sydney time
export function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const date = toSydneyTime(dateStr);
  if (!date) return dateStr;
  
  const datePart = date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: SYDNEY_TIMEZONE
  }).replace(/\s/g, '-');
  
  const timePart = date.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: SYDNEY_TIMEZONE
  });
  
  return `${datePart} ${timePart}`;
}

// Format for calendar events (shows day name) in Sydney time
export function formatEventDate(dateStr, isAllDay = false) {
  if (!dateStr) return '';
  const date = toSydneyTime(dateStr);
  if (!date) return dateStr;
  
  const dayName = date.toLocaleDateString('en-AU', { 
    weekday: 'short',
    timeZone: SYDNEY_TIMEZONE 
  });
  
  if (isAllDay) {
    return `${dayName} ${formatDate(dateStr)}`;
  }
  return `${dayName} ${formatDateTime(dateStr)}`;
}

// Full date format with time for logs: DD MMM YYYY, HH:MM AEST/AEDT
export function formatLogDateTime(dateStr) {
  if (!dateStr) return '';
  const date = toSydneyTime(dateStr);
  if (!date) return dateStr;
  
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: SYDNEY_TIMEZONE,
    timeZoneName: 'short'
  });
}
